import React, { ReactNode, useState } from 'react';
import { Table, Button } from '@ver-uds/react';
import type { Column } from 'react-table';
import { Hint, TextInputControl } from '@ver-uds/uswds-react';
import { useFormContext } from 'react-hook-form';
import { useNaicsSubSectorByIdQuery, useRefDataNAICSSectorApi } from '../services';
import LookupNaicsCodeModal from './LookupNaicsCodeModal';
import HelpModal, { HelpModalProps } from './HelpModal';
import Section from './Section';
import LabelHint from './Enrollment/CompanyInformation/LabelHint';

export type FormValues = {
  naicsCode: string;
};

export type NaicsCodeTableProps = {
  label: string;
};

const NaicsCodeTable = ({ label }: NaicsCodeTableProps): JSX.Element => {
  const naicsSectorApi = useRefDataNAICSSectorApi();
  const [naicsModalIsOpen, setNaicsModalIsOpen] = useState(false);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch, register, errors, setValue } = useFormContext<FormValues>();

  const { naicsCode } = watch();

  const naicsSubSectorCode = naicsCode?.length === 3 ? naicsCode : undefined;
  const { data: naicsSubSector } = useNaicsSubSectorByIdQuery(naicsSubSectorCode, {
    useErrorBoundary: false,
  });

  const [helpModalIsOpen, setHelpModalIsOpen] = useState(false);

  function initializeNode(): React.ReactNode {
    return <></>;
  }
  const [getModalContent, setModalContent] = useState({ header: '', body: initializeNode() });

  const openModal = (content: HelpModalProps['modalContent']) => {
    setModalContent(content);
    setHelpModalIsOpen(true);
  };

  const naicsCodeHeader = (
    <>
      NAICS Code <Hint>(required)</Hint> <LabelHint openModal={openModal} label={label} />
    </>
  );

  type TableData = {
    naicsCodeInput: ReactNode;
    codeDisplay: ReactNode;
  };

  const tableColumns: Column<TableData>[] = [
    {
      Header: naicsCodeHeader,
      accessor: 'naicsCodeInput',
      width: 100,
    },
    {
      Header: 'Description',
      accessor: 'codeDisplay',
      width: 200,
    },
  ];

  const tableData: TableData[] = [
    {
      naicsCodeInput: (
        <div className="grid-row flex-align-items mobile:flex-no-wrap">
          <div className="tablet:grid-col-6 grid-col-12">
            <TextInputControl
              className="margin-0"
              id="naics-code-input"
              aria-label="NAICS Code"
              name="naicsCode"
              ref={register({
                required: {
                  value: true,
                  message: 'Please enter a 3-digit code.',
                },
                pattern: {
                  value: /^\d{3}$/,
                  message: 'Please enter a 3-digit code.',
                },
                validate: async (value: string) => {
                  if (value?.length === 3) {
                    try {
                      await naicsSectorApi.getNaicsSubSectorsById({ naicsSubSector: value });
                      return true;
                    } catch (e) {
                      return 'Please enter a valid NAICS code.';
                    }
                  }
                  return true;
                },
              })}
              errorMessage={errors.naicsCode?.message}
              errorId={errors.naicsCode && 'naics-code-error'}
              style={{
                width: '74px',
                height: '40px',
              }}
            />
          </div>
          <div className="tablet:grid-col-6 grid-col-12 tablet:margin-top-0 margin-top-1 display-flex flex-align-center">
            <Button
              type="button"
              variant="tertiary"
              name="test"
              aria-label="Open modal to look up NAICS code"
              onClick={(): void => setNaicsModalIsOpen(true)}
            >
              Look up a Code
            </Button>
          </div>
        </div>
      ),
      codeDisplay: (
        <div className="height-full display-flex flex-align-center">{naicsSubSector?.displayName ?? '---'}</div>
      ),
    },
  ];

  return (
    <Section title="NAICS Code">
      <Table id="naics-code-table" variant="default" columns={tableColumns} data={tableData} />
      <LookupNaicsCodeModal
        open={naicsModalIsOpen}
        close={(): void => setNaicsModalIsOpen(false)}
        onSubmit={(modalFormValues): void => {
          setValue('naicsCode', modalFormValues.naicsSubSector, { shouldValidate: true, shouldDirty: true });
        }}
      />
      <HelpModal
        modalContent={getModalContent}
        open={helpModalIsOpen}
        close={(): void => {
          setHelpModalIsOpen(false);
        }}
      />
    </Section>
  );
};

export default NaicsCodeTable;
